import { el, clear, fmtDataHora } from '../lib/dom.js';
import { historicoMovimentacoes } from '../domain/movimentacao.js';
import { renderTabela } from '../ui/table.js';
import { badgeTipo } from './dashboard.js';
import { toast } from '../lib/toast.js';
import { buscaSelect } from '../ui/buscaSelect.js';
import { supabase } from '../config/supabaseClient.js';
import { buscarTodos } from '../lib/db.js';

/** Pesquisa/Histórico de movimentações: período + chave + busca, com pessoa/motivo/operador. */
// Coluna 'Pessoa' por tipo: RETIRADA = quem pegou; DEVOLUCAO = quem devolveu.
function cellPessoa(l) {
  if (l.tipo === 'DEVOLUCAO') return l.devolvido_por || l.colaborador || '—';
  return l.colaborador || '—';
}
// Coluna 'Detalhes' por tipo: RETIRADA mostra o motivo; DEVOLUCAO mostra de quem a chave estava.
function cellDetalhes(l) {
  if (l.tipo === 'DEVOLUCAO') return l.colaborador ? 'Estava com ' + l.colaborador : '—';
  return l.motivo ? 'Motivo: ' + l.motivo : '—';
}

async function opcoesChaves() {
  try {
    const data = await buscarTodos(() => supabase.from('chave').select('id, numero, descricao').order('numero'));
    return data.map((c) => ({ value: c.id, label: `${c.numero}${c.descricao ? ' — ' + c.descricao : ''}` }));
  } catch { return []; }
}

export const paginaPesquisa = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Pesquisa / Histórico' }),
      el('p', { class: 'view__sub', text: 'Consulte as movimentações de chaves por período e por chave.' }),
    );

    const de = el('input', { type: 'date' });
    const ate = el('input', { type: 'date' });
    de.addEventListener('change', () => { periodo.value = 'custom'; ate.min = de.value || ''; if (de.value && ate.value && ate.value < de.value) ate.value = de.value; });
    ate.addEventListener('change', () => { periodo.value = 'custom'; de.max = ate.value || ''; if (ate.value && de.value && de.value > ate.value) de.value = ate.value; });
    const periodo = el('select', {}, [
      el('option', { value: '7d' }, 'Últimos 7 dias'),
      el('option', { value: 'mes' }, 'Este mês'),
      el('option', { value: '12m' }, 'Últimos 12 meses'),
      el('option', { value: 'ano' }, 'Este ano'),
      el('option', { value: 'custom' }, 'Personalizado'),
    ]);
    function isoData(d) { return d.toISOString().slice(0, 10); }
    function aplicarPreset() {
      const pp = periodo.value;
      if (pp === 'custom') { carregar(); return; }
      const hoje = new Date(); let ini = new Date(hoje);
      if (pp === '7d') ini.setDate(hoje.getDate() - 6);
      else if (pp === 'mes') ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      else if (pp === '12m') ini.setMonth(hoje.getMonth() - 12);
      else if (pp === 'ano') ini = new Date(hoje.getFullYear(), 0, 1);
      de.value = isoData(ini); ate.value = isoData(hoje);
      de.removeAttribute('max'); ate.removeAttribute('min');
      carregar();
    }
    periodo.addEventListener('change', aplicarPreset);

    const chaves = await opcoesChaves();
    const pickerChave = buscaSelect({ opcoes: chaves, limite: 1000, aoEscolher: () => carregar() });

    const btn = el('button', { class: 'btn btn--primario' }, [el('i', { class: 'ph ph-magnifying-glass' }), ' Filtrar']);
    const limpar = el('button', { class: 'btn', title: 'Limpar filtro' }, [el('i', { class: 'ph ph-eraser' }), ' Limpar']);
    limpar.addEventListener('click', () => { periodo.value = '7d'; pickerChave.limpar(); aplicarPreset(); });

    container.append(el('div', { class: 'painel', style: 'margin-bottom:16px;display:flex;gap:16px;align-items:flex-end;flex-wrap:wrap;' }, [
      el('div', { class: 'campo', style: 'margin:0;' }, [el('label', { text: 'Período' }), periodo]),
      el('div', { class: 'campo', style: 'margin:0;' }, [el('label', { text: 'De' }), de]),
      el('div', { class: 'campo', style: 'margin:0;' }, [el('label', { text: 'Até' }), ate]),
      el('div', { class: 'campo', style: 'margin:0; min-width:220px;' }, [el('label', { text: 'Chave' }), pickerChave.elemento]),
      btn,
      limpar,
    ]));

    const tabelaBox = el('div');
    container.append(tabelaBox);

    async function carregar() {
      const filtros = { limite: 500 };
      if (de.value) filtros.de = new Date(de.value + 'T00:00:00').toISOString();
      if (ate.value) filtros.ate = new Date(ate.value + 'T23:59:59').toISOString();
      const cid = pickerChave.getValue();
      if (cid) filtros.chave_id = cid;
      const dados = await historicoMovimentacoes(filtros);
      if (dados.length >= 500) toast('Mostrando as 500 movimentações mais recentes do período. Refine as datas ou filtre por chave para ver as demais.', 'info');
      renderTabela(tabelaBox, {
        colunas: [
          { chave: 'data_hora', rotulo: 'Data/hora', render: (l) => fmtDataHora(l.data_hora) },
          { chave: 'tipo', rotulo: 'Tipo', render: (l) => badgeTipo(l.tipo) },
          { chave: 'chave', rotulo: 'Chave' },
          { chave: 'pessoa', rotulo: 'Pessoa', ordenavel: false, render: cellPessoa },
          { chave: 'detalhes', rotulo: 'Detalhes', ordenavel: false, render: cellDetalhes },
          { chave: 'operador', rotulo: 'Operador', render: (l) => l.operador || '—' },
          { chave: 'observacao', rotulo: 'Obs.', render: (l) => l.observacao || '—' },
        ],
        linhas: dados,
        buscarEm: ['chave', 'colaborador', 'motivo', 'operador', 'devolvido_por', 'observacao'],
        porPagina: 15,
      });
    }

    btn.addEventListener('click', carregar);
    periodo.value = '7d'; aplicarPreset();
  },
};